import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { AppShell } from '@/components/layout/app-shell'
import { ClientDialog } from '@/components/clients/client-dialog'
import { ConfirmDialog } from '@/components/shared/confirm-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useApi } from '@/hooks/use-api'
import { type ApiClient, ApiError, type PaginatedResponse } from '@/lib/api'
import { cn } from '@/lib/utils'

/**
 * Clients admin page — full CRUD.
 *
 * Pattern (re-used by Vehicles + Drivers in this same passe):
 *
 *   1. Header : title + "Nouveau" button.
 *   2. Search bar (debounced 300 ms).
 *   3. Card with the table.
 *   4. Pagination footer.
 *   5. Create / edit dialog (component).
 *   6. Delete confirmation (shared `ConfirmDialog`).
 *
 * The table renders the same shape returned by
 * `GET /api/clients` — including `phone`, `address`, `siret`,
 * `color`. The list endpoint already returns them; no extra GET
 * required for editing.
 */

interface ClientRow {
  id: string
  name: string
  email: string | null
  phone: string | null
  address: string | null
  siret: string | null
  color: string
  createdAt: string
  updatedAt: string
}

const PAGE_SIZE = 20
const QUERY_NS = 'clients' as const

export function ClientsPage() {
  const api = useApi()
  const queryClient = useQueryClient()

  /* -------- search (debounced) -------- */
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    if (searchDraft === search) return
    const t = setTimeout(() => setSearch(searchDraft), 300)
    return () => clearTimeout(t)
  }, [searchDraft, search])

  /* -------- pagination -------- */
  const [page, setPage] = useState(1)
  // Reset to page 1 when the search committed value changes.
  useEffect(() => {
    setPage(1)
  }, [search])

  /* -------- dialog state -------- */
  const [editing, setEditing] = useState<ClientRow | null | undefined>(undefined)
  const [pendingDelete, setPendingDelete] = useState<ClientRow | null>(null)

  /* -------- list query -------- */
  const queryKey = useMemo(
    () => [QUERY_NS, 'list', search, page] as const,
    [search, page],
  )

  const listQuery = useQuery({
    queryKey,
    queryFn: () => fetchClients(api, search, page, PAGE_SIZE),
    placeholderData: (prev: PaginatedResponse<ClientRow> | undefined) => prev,
    staleTime: 30 * 1000,
  })

  /* -------- delete mutation -------- */
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/clients/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_NS] })
      setPendingDelete(null)
    },
  })

  const rows = listQuery.data?.data ?? []
  const total = listQuery.data?.meta.total ?? 0
  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text">Clients</h1>
          <p className="text-sm text-text-muted">
            Gérez votre annuaire client.
          </p>
        </div>
        <Button onClick={() => setEditing(null)}>
          <Plus size={16} />
          Nouveau client
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent>
          <div className="relative max-w-md">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
              aria-hidden="true"
            />
            <Input
              type="search"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder="Rechercher un client par nom"
              className="pl-9"
            />
            {searchDraft !== '' ? (
              <button
                type="button"
                aria-label="Effacer la recherche"
                onClick={() => {
                  setSearchDraft('')
                  setSearch('')
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-text-muted hover:text-text hover:bg-surface-hover"
              >
                <X size={14} aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        {listQuery.isLoading ? (
          <div className="py-16 flex justify-center">
            <Spinner size={28} label="Chargement des clients" />
          </div>
        ) : listQuery.isError ? (
          <div className="px-6 py-12 text-center text-danger">
            Impossible de charger les clients.
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-12 text-center text-text-muted">
            <Building2 size={28} className="mx-auto mb-2 opacity-60" aria-hidden="true" />
            <p>Aucun client ne correspond à la recherche.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-text-muted">
                <tr className="text-left">
                  <Th className="w-12" srOnly>
                    Couleur
                  </Th>
                  <Th>Nom</Th>
                  <Th>Email</Th>
                  <Th>Téléphone</Th>
                  <Th>SIRET</Th>
                  <Th className="w-12" srOnly>
                    Actions
                  </Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <ClientRow
                    key={row.id}
                    row={row}
                    onEdit={() => setEditing(row)}
                    onDelete={() => setPendingDelete(row)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {total > 0 ? (
        <div className="mt-4 flex items-center justify-between text-sm text-text-muted">
          <p>
            Affichage {(page - 1) * PAGE_SIZE + 1}–
            {Math.min(page * PAGE_SIZE, total)} sur {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || listQuery.isFetching}
            >
              <ChevronLeft size={14} />
              Précédent
            </Button>
            <span className="px-2 text-text">
              {page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages || listQuery.isFetching}
            >
              Suivant
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      ) : null}

      {/* Create / edit */}
      <ClientDialog
        client={editing}
        open={editing !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined)
        }}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null)
            deleteMutation.reset()
          }
        }}
        title="Supprimer le client ?"
        description="Le client ne pourra plus être consulté ni associé à de nouveaux plannings. Les missions et plannings historiques restent intacts."
        confirmLabel="Supprimer"
        tone="danger"
        loading={deleteMutation.isPending}
        errorMessage={
          deleteMutation.error instanceof ApiError
            ? deleteMutation.error.message
            : null
        }
        onConfirm={() => {
          if (pendingDelete !== null) deleteMutation.mutate(pendingDelete.id)
        }}
      >
        {pendingDelete !== null ? (
          <div className="text-sm text-text">
            <p className="font-medium inline-flex items-center gap-2">
              <span
                aria-hidden="true"
                className="w-3 h-3 rounded-full"
                style={{ background: pendingDelete.color }}
              />
              {pendingDelete.name}
            </p>
            {pendingDelete.email !== null ? (
              <p className="text-text-muted text-xs mt-1">{pendingDelete.email}</p>
            ) : null}
          </div>
        ) : null}
      </ConfirmDialog>
    </AppShell>
  )
}

/* -------------------------------------------------------------------------- */
/*  Row component                                                              */
/* -------------------------------------------------------------------------- */

interface ClientRowProps {
  row: ClientRow
  onEdit: () => void
  onDelete: () => void
}

function ClientRow({ row, onEdit, onDelete }: ClientRowProps) {
  return (
    <tr className="border-t border-surface-border hover:bg-surface-hover transition-colors">
      <Td className="w-12">
        <span
          aria-hidden="true"
          className="inline-block w-4 h-4 rounded-full border border-surface-border"
          style={{ background: row.color }}
        />
      </Td>
      <Td className="font-medium text-text">{row.name}</Td>
      <Td className="text-text-muted">{row.email ?? '—'}</Td>
      <Td className="text-text-muted">{row.phone ?? '—'}</Td>
      <Td className="text-text-muted font-mono">{row.siret ?? '—'}</Td>
      <Td className="w-12 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Actions pour ${row.name}`}
              className="p-1.5 rounded-md text-text-muted hover:bg-surface-hover hover:text-text"
            >
              <MoreVertical size={16} aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onEdit()}>
              <Pencil size={14} aria-hidden="true" />
              Modifier
            </DropdownMenuItem>
            <DropdownMenuItem danger onSelect={() => onDelete()}>
              <Trash2 size={14} aria-hidden="true" />
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Td>
    </tr>
  )
}

/* -------------------------------------------------------------------------- */
/*  Table cell helpers                                                         */
/* -------------------------------------------------------------------------- */

function Th({
  className,
  children,
  srOnly = false,
}: {
  className?: string
  children: React.ReactNode
  srOnly?: boolean
}) {
  return (
    <th
      scope="col"
      className={cn(
        'px-3 py-2 font-medium uppercase tracking-wide text-xs',
        className,
      )}
    >
      {srOnly ? <span className="sr-only">{children}</span> : children}
    </th>
  )
}

function Td({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return <td className={cn('px-3 py-2.5', className)}>{children}</td>
}

/* -------------------------------------------------------------------------- */
/*  Fetcher                                                                    */
/* -------------------------------------------------------------------------- */

async function fetchClients(
  api: ApiClient,
  search: string,
  page: number,
  limit: number,
): Promise<PaginatedResponse<ClientRow>> {
  const params: Record<string, string | number | boolean> = {
    page,
    limit,
    sortBy: 'name',
    sortOrder: 'asc',
  }
  if (search.trim() !== '') params.search = search.trim()
  return api.getPaginated<ClientRow>('/api/clients', params)
}
